<template>
  <div
    class="calculator"
    :class="{ loc: productTypeId === ProductIds.LOC, pel: productTypeId === ProductIds.PEL }"
  >
    <a class="close" @click="close">
      <icon icon="close" />
    </a>
    <h3>Estimated Cost of Attendance</h3>
    <p>
      The cost of attendance listed is an estimated amount associated with these types of programs.
      Actual costs may vary based on the specific program, remaining years of study, and location.
      Please consult your school's admissions or financial aid office for your official cost of
      attendance.
    </p>
    <div class="school-tuition">
      <div class="school-info">
        <p>
          {{ info?.school?.schoolName ? info?.school?.schoolName : school }}
        </p>
        <p>
          {{ term ? term : "Not Yet Selected" }}
        </p>
      </div>
      <div class="tuition-costs">
        <p>
          <span :class="{ active: tuitionType === 'in-state' }" @click="tuitionType = 'in-state'">
            In State
          </span>
          |
          <span
            :class="{ active: tuitionType === 'out-of-state' }"
            @click="tuitionType = 'out-of-state'"
          >
            Out of State
          </span>
        </p>
        <p class="cost">${{ isNaN(formatNumber(tuition)) ? formatNumber(tuition) : "" }}</p>
      </div>
    </div>
    <div class="additional-funding">
      <h3>How Much Funding Do You Already Have?</h3>
      <div class="funding-field">
        <label>Scholarships & Grants</label>
        <base-form-field
          v-model="funding.scholarshipsAndGrants"
          :schema="schema.scholarshipsAndGrants"
        />
      </div>
      <div class="funding-field">
        <label>
          Federal & Private Loans
          <span class="hint">
            Not including this {{ productTypeId === ProductIds.LOC ? "line" : "loan" }}
          </span>
        </label>
        <base-form-field v-model="funding.loans" :schema="schema.loans" />
      </div>
      <div class="funding-field">
        <label>
          Savings
          <span class="hint"> Savings or money from family & friends that you plan to use </span>
        </label>
        <base-form-field v-model="funding.savings" :schema="schema.savings" />
      </div>
    </div>
    <div class="difference">
      <h4>Difference</h4>
      <div class="remaining-balance">
        <p>${{ isNaN(formatNumber(difference)) ? formatNumber(difference) : "" }}</p>
        <a @click="useThisAmount">Use this amount</a>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from "vue";
import useFormatter from "../../use/formatter";
import BaseFormField from "../shared/BaseFormField.vue";
import Icon from "../shared/Icon.vue";
import { mapActions, mapState } from "vuex";
import { School } from "@/models/school";
import { $api } from "@/services/api";
import { FormSchema } from "@/models/form";
import { ProductIds, CustomSchoolTypes } from "@/config/constants";
import { useStore } from "vuex";

export default defineComponent({
  setup() {
    const store = useStore();

    const { formatNumber } = useFormatter();

    const productTypeId = computed<number>(() => store.state.appData?.application?.productTypeId);

    const previouslySelectedSchool = computed(
      () => store.getters["loanConfigurationModule/selectedSchool"],
    );

    return {
      formatNumber,
      store,
      previouslySelectedSchool,
      productTypeId,
      ProductIds,
      CustomSchoolTypes,
    };
  },
  props: {
    application: {
      type: Object,
      required: true,
    },
    term: {
      type: String,
      required: true,
    },
    info: {
      type: Object,
    },
    isCurrentSubStep: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
    },
  },
  components: { BaseFormField, Icon },
  data: () => ({
    selectedSchool: {} as School,
    tuitionType: "in-state",
    gettingData: true,
    funding: {
      scholarshipsAndGrants: 0 as undefined | number,
      loans: 0 as undefined | number,
      savings: 0 as undefined | number,
    } as any,
    schema: {
      scholarshipsAndGrants: {
        name: "scholarshipsAndGrants",
        type: "money",
        hideLabel: true,
        default: 0,
        required: false,
      },
      loans: {
        name: "loans",
        type: "money",
        hideLabel: true,
        default: 0,
        required: false,
      },
      savings: {
        name: "savings",
        type: "money",
        hideLabel: true,
        default: 0,
        required: false,
      },
    } as FormSchema,
  }),
  watch: {
    tuitionCosts(newValue, oldValue) {
      console.log("tuitionCosts", "old", oldValue, "new", newValue);
      if (isNaN(newValue.inState)) {
        this.gettingData = true;
      } else {
        this.gettingData = false;
      }
    },
  },
  computed: {
    ...mapState(["appData", "schools"]),
    school(): any {
      return this.appData?.application?.schoolConfiguration?.schoolName;
    },
    customSchoolType(): boolean {
      return CustomSchoolTypes.includes(this.selectedSchool?.schoolTypeId);
    },
    customCombinedTerms(): boolean {
      return (this.appData?.application?.schoolConfiguration?.academicTerm || "")
        ?.toLowerCase()
        ?.includes("term starting in");
    },
    combinedTerms(): boolean {
      if (this.appData?.application?.productTypeId === ProductIds.PEL) {
        const enrollmentTerm =
          this.store.state.loanConfigurationModule.applicationInfo?.selectedEnrollmentTerm
            ?.description ||
          this.appData?.application?.schoolConfiguration?.academicTerm ||
          "";

        return enrollmentTerm?.includes("-");
      }

      return (this.appData?.application?.schoolConfiguration?.academicTerm || "").includes("-");
    },
    tuitionCosts(): any {
      const tuitionCosts = {} as any;
      this.setSchoolInfo();

      tuitionCosts.inState =
        this.splitTuition === false
          ? this.selectedSchool?.costOfAttendanceInState
          : this.selectedSchool?.costOfAttendanceInState / 2;

      tuitionCosts.outOfState =
        this.splitTuition === false
          ? this.selectedSchool?.costOfAttendanceOutOfState
          : this.selectedSchool?.costOfAttendanceOutOfState / 2;

      return tuitionCosts;
    },
    tuition(): number {
      return this.tuitionType === "in-state"
        ? !isNaN(this.tuitionCosts.inState)
          ? this.tuitionCosts.inState
          : 0
        : !isNaN(this.tuitionCosts.outOfState)
          ? this.tuitionCosts.outOfState
          : 0;
    },
    splitTuition(): boolean {
      if (this.combinedTerms === true || this.customCombinedTerms === true || this.customSchoolType === true) {
        return false;
      }
      return true;
    },
    sum(): number {
      return (
        Object.keys(this.funding).reduce((previous, key) => {
          if (!this.funding[key]) {
            return previous;
          } else {
            return previous + parseFloat(this.funding[key]) || 0;
          }
        }, 0) || 0
      );
    },
    difference(): number {
      return this.tuition - this.sum;
    },
  },
  methods: {
    ...mapActions(["setSchools"]),
    async setSchoolInfo() {
      if (this.appData?.application?.productTypeId === ProductIds.PEL) {
        this.selectedSchool = this.previouslySelectedSchool;
      } else {
        if (this.schools.length <= 0) {
          const res = await $api.school.getSchoolsByProgram(
            this.appData?.application?.programTypeId,
            this.appData?.application?.productTypeId,
            this.appData?.application?.lenderId,
          );
          this.setSchools(res.schools);
        }

        const school = this.schools.find(
          (s: School) =>
            s.id === this.appData?.application?.schoolConfiguration?.schoolId ||
            s.id === this.appData?.application?.schoolId ||
            s.schoolName === this.appData?.application.schoolConfiguration?.schoolName,
        );
        this.selectedSchool = school || {};
      }
    },
    useThisAmount() {
      this.$emit("useThisAmount", Math.round(this.difference));
    },
    close() {
      this.$emit("close", true);
    },
  },
});
</script>

<style lang="scss" scoped>
.calculator {
  max-width: 98%;
  margin: 0 auto;
  position: relative;
  padding-top: 12px;

  .close {
    position: absolute;
    top: 0;
    right: -25px;
    font-weight: 700;
    font-size: 1.1em;
    cursor: pointer;
    padding-top: 8px;

    &:active {
      font-size: 1.05em;
    }
  }

  .school-tuition {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    padding-bottom: 1em;
    border-bottom: 1px solid #767676;

    .school-info {
      p {
        margin: 0 0 0.5em;
        color: #767676;
      }
    }

    .tuition-costs {
      text-align: right;

      p {
        margin: 0;

        &.cost {
          font-size: 1.4em;
          font-weight: 700;
          margin-top: 0.5em;
        }

        span {
          color: #767676;
          cursor: pointer;

          &.active {
            font-weight: 700;
            color: #000;
          }
        }
      }
    }
  }

  .additional-funding {
    border-bottom: 1px solid #767676;
    padding-bottom: 1em;

    .funding-field {
      display: flex;
      justify-content: space-between;
      padding: 0;
      margin-bottom: 1em;

      label {
        font-size: 1em;
        font-weight: 700;
        max-width: 40%;
        display: flex;
        flex-direction: column;

        .hint {
          font-size: 0.8em;
          font-weight: 400;
          color: #767676;
        }
      }

      .form-field {
        max-width: 30%;
      }
    }
  }

  .difference {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-top: 1em;

    h4 {
      margin: 0;
    }

    .remaining-balance {
      p {
        margin: 0;
        font-weight: 700;
        font-size: 1.4em;
      }

      a {
        text-decoration: underline;
        cursor: pointer;
        color: #767676;
        font-size: 0.9em;
      }
    }
  }
}

@media (max-width: 480px) {
  .calculator:is(.pel) .close {
    padding-right: 8px;
  }
}
</style>
